<script>
import LinkTenantSettingActionLink from "@/components/pc/setting/linktenant/LinkTenantSettingActionLink.vue";
import constants from "@/components/constants";
import commonUtil from "@/components/commonutil";

export default {
    name: "LinkTenantSettingTable",
    emits: [
        "connectRequest",
        "disconnectRequest",
        "confirmAllowRequest",
        "deleteLinkRequest",
        "listGroupAndUserRequest",
        "advancedSettingsRequest",
    ],
    components: {LinkTenantSettingActionLink},
    props: {
        myTenantName: {
            type: String,
            required: true,
            default: () => "",
        },
        tenantList: {
            type: Array,
            required: true,
            default: () => [],
        },
        settingType: {
            type: Number,
            required: true,
            default: constants.SETTING_INTER_COMPANY_IMPORT,
        },
        isTenantLinkLimitReached: {
            type: Boolean,
            required: true,
            default: false,
        },
    },
    computed: {
        linkAdminHelp() {
            return this.isImportTenantSetting ? this.$t('message.hint_admin_accessible_tenant') : this.$t('message.hint_admin_allowed_tenant');
        },
        isImportTenantSetting() {
            return this.settingType === constants.SETTING_INTER_COMPANY_IMPORT;
        },
    },
    methods: {
        isLinkWanted(tenant) {
            return tenant.state === constants.TENANT_CONNECT_WANT;
        },
        isLinkAllowed(tenant) {
            return tenant.state === constants.TENANT_CONNECT_ALLOW;
        },
        isLinkEstablished(tenant) {
            return tenant.state === constants.TENANT_CONNECT_ESTABLISH;
        },
        getTenantName(tenant) {
            return `${tenant.tenantName}${tenant.deletedOrTerminated ? this.$t('message.item_link_end') : ""}`;
        },
        getWantUserName(tenant) {
            const tenantName = this.isImportTenantSetting ? this.myTenantName : tenant.tenantName;
            return this.isLinkAllowed(tenant) ? "-" : `${tenant.wantUserName}@${tenantName}`;
        },
        getLinkStatusLabel(state) {
            return commonUtil.getLinkStatusName(
                this.$i18n,
                this.isImportTenantSetting ? "import" : "export",
                state
            );
        },
        emitConnectRequest(tenant) {
            this.$emit("connectRequest", "import", tenant);
        },
        emitDisconnectRequest(type, tenant) {
            this.$emit("disconnectRequest", type, tenant);
        },
        emitConfirmAllowRequest(tenant) {
            this.$emit("confirmAllowRequest", tenant);
        },
        emitDeleteLinkRequest(type, tenant) {
            this.$emit('deleteLinkRequest', type, tenant);
        },
        emitListGroupAndUserRequest(type, tenant) {
            this.$emit('listGroupAndUserRequest', type, tenant);
        },
        emitAdvancedSettingsRequest(tenant) {
            this.$emit('advancedSettingsRequest', tenant);
        },
    },
};
</script>

<template>
    <div class="tenant-list__table tenant-list__table--col5">
        <div class="tenant-list__table-header-cell tenant-list__table-header-cell--first object-title"
             v-t="'message.item_tenant_name'"></div>
        <div class="tenant-list__table-header-cell object-title">
            <span v-t="'message.item_link_admin'"/>
            <el-tooltip effect="dark" placement="top-start">
                <div slot="content" v-html="linkAdminHelp"></div>
                <img
                    class="icon-help"
                    :src="require('@/assets/icon_help.svg')"
                    alt="Link administrator can import the user of theses tenants to own tenant's group"
                />
            </el-tooltip>
        </div>
        <div class="tenant-list__table-header-cell tenant-list__table-header-cell-status object-title"
             v-t="'message.item_status'"></div>
        <div class="tenant-list__table-header-cell object-title"></div>
        <div class="tenant-list__table-header-cell object-title"></div>
        <div class="tenant-list__table-header-cell tenant-list__table-header-cell--last object-title"></div>
        <template v-for="tenant in tenantList">
            <div
                class="tenant-list__table-body-cell tenant-list__table-body-cell--first tenant-list__body-tenant-name"
            >
                {{ getTenantName(tenant) }}
            </div>
            <div class="tenant-list__table-body-cell tenant-list__body-want-user-name">
                {{ getWantUserName(tenant) }}
            </div>
            <div class="tenant-list__table-body-cell tenant-list__body-link-state">
                {{ getLinkStatusLabel(tenant.state) }}
            </div>
            <div class="tenant-list__table-body-cell tenant-list__table-body-cell-action">
                <LinkTenantSettingActionLink
                    v-if="isLinkEstablished(tenant)"
                    @click="emitAdvancedSettingsRequest(tenant)"
                >
                    {{ $t("message.action_advanced_settings") }}
                </LinkTenantSettingActionLink>
            </div>
            <template v-if="tenant.deletedOrTerminated">
                <div class="tenant-list__table-body-cell tenant-list__table-body-cell-action"></div>
                <div
                    class="tenant-list__table-body-cell tenant-list__table-body-cell--last tenant-list__table-body-cell-action"
                >
                    <LinkTenantSettingActionLink
                        @click="emitDeleteLinkRequest(isImportTenantSetting ? 'importForceUnLink' : 'exportForceUnLink', tenant)"
                        variant="danger"
                    >
                        {{ $t("message.action_delete_link") }}
                    </LinkTenantSettingActionLink>
                </div>
            </template>
            <template v-else-if="isImportTenantSetting">
                <div class="tenant-list__table-body-cell tenant-list__table-body-cell-action">
                    <LinkTenantSettingActionLink
                        v-if="isLinkEstablished(tenant)"
                        @click="emitListGroupAndUserRequest('import', tenant)"
                    >
                        {{ $t("message.action_confirm") }}
                    </LinkTenantSettingActionLink>
                    <el-tooltip
                        v-else-if="isLinkAllowed(tenant)"
                        :disabled="!isTenantLinkLimitReached"
                        effect="dark"
                        placement="top-start"
                    >
                        <div slot="content" v-t="'message.error_exceeds_max_linked_tenants'"></div>
                        <div>
                            <LinkTenantSettingActionLink
                                :disabled="isTenantLinkLimitReached"
                                @click="emitConnectRequest(tenant)"
                            >
                                {{ $t("message.action_connect_link") }}
                            </LinkTenantSettingActionLink>
                        </div>
                    </el-tooltip>
                </div>
                <div
                    class="tenant-list__table-body-cell  tenant-list__table-body-cell--last tenant-list__table-body-cell-action"
                >
                    <LinkTenantSettingActionLink
                        v-if="isLinkEstablished(tenant)"
                        @click="emitDisconnectRequest('import', tenant)"
                        variant="danger"
                    >
                        {{ $t("message.action_disconnect_link") }}
                    </LinkTenantSettingActionLink>
                    <LinkTenantSettingActionLink
                        v-else
                        @click="emitDeleteLinkRequest('import', tenant)"
                        variant="danger"
                    >
                        {{ $t("message.action_delete_link") }}
                    </LinkTenantSettingActionLink>
                </div>
            </template>
            <template v-else>
                <div class="tenant-list__table-body-cell tenant-list__table-body-cell-action">
                    <LinkTenantSettingActionLink
                        v-if="isLinkEstablished(tenant)"
                        @click="emitListGroupAndUserRequest('export', tenant)"
                    >
                        {{ $t("message.action_confirm") }}
                    </LinkTenantSettingActionLink>
                    <el-tooltip
                        v-else-if="isLinkWanted(tenant)"
                        :disabled="!isTenantLinkLimitReached"
                        effect="dark"
                        placement="top-start"
                    >
                        <div slot="content" v-t="'message.error_exceeds_max_linked_tenants'"></div>
                        <div>
                            <LinkTenantSettingActionLink
                                :disabled="isTenantLinkLimitReached"
                                @click="emitConfirmAllowRequest(tenant)"
                            >
                                {{ $t("message.action_connect_link") }}
                            </LinkTenantSettingActionLink>
                        </div>
                    </el-tooltip>
                </div>
                <div
                    class="tenant-list__table-body-cell tenant-list__table-body-cell--last tenant-list__table-body-cell-action"
                >
                    <LinkTenantSettingActionLink
                        v-if="isLinkEstablished(tenant)"
                        @click="emitDisconnectRequest('export', tenant)"
                        variant="danger"
                    >
                        {{ $t("message.action_disconnect_link") }}
                    </LinkTenantSettingActionLink>
                    <LinkTenantSettingActionLink
                        v-else
                        @click="emitDeleteLinkRequest('export', tenant)"
                        variant="danger"
                    >
                        {{ $t("message.action_delete_link") }}
                    </LinkTenantSettingActionLink>
                </div>
            </template>
        </template>
    </div>
</template>

<style scoped>
.tenant-list__table {
    display: grid;

    &.tenant-list__table--col4 {
        grid-template-columns: minmax(min-content, 1fr) minmax(min-content, 1fr) max-content max-content max-content;
    }

    &.tenant-list__table--col5 {
        grid-template-columns: minmax(min-content, 1fr) minmax(min-content, 1fr) max-content max-content max-content max-content;
    }

    .tenant-list__table-header-cell, .tenant-list__table-body-cell {
        padding: 10px;
        text-align: left;

        &.tenant-list__table-header-cell--first, &.tenant-list__table-body-cell--first {
            padding-left: 20px;
        }

        &.tenant-list__table-header-cell--last, &.tenant-list__table-body-cell--last {
            padding-right: 20px;
        }
    }

    .tenant-list__table-header-cell {
        background-color: #f6f6f6;
        color: #848991;
        font-size: 12px;
        white-space: nowrap;
        position: sticky;
        top: 0;

        &.tenant-list__table-header-cell-status {
            text-align: center;
        }
    }

    .tenant-list__table-body-cell {
        display: flex;
        align-items: center;
        color: #848991;

        &.tenant-list__body-tenant-name, &.tenant-list__body-want-user-name {
            word-break: break-all;
        }

        &.tenant-list__body-link-state {
            justify-content: center;
        }

        &.tenant-list__table-body-cell-action {
            justify-content: center;
        }
    }
}
</style>
